import React, { type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
// @ts-expect-error - TS2305 - Module '"@atlaskit/onboarding"' has no exported member 'Action'.
import type { Action } from '@atlaskit/onboarding';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { BenefitsModal } from '@atlassian/jira-servicedesk-itsm-onboarding-common';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { ExperienceSuccess } from '@atlassian/ufo';
import { yourWorkFirstStepExperience } from '../../../experiences';
import { useYourWorkTour } from '../../../services';
import { Animation } from './animation';
import messages from './messages';

export const SCREEN_NAME = 'jsmYourWorkTourWelcomeModal';

export const WelcomeModal = () => {
	const { formatMessage } = useIntl();
	const [, { end, next }] = useYourWorkTour();

	const onClickNext = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, `${SCREEN_NAME}Button`, {
			buttonType: 'next',
		});
		next('modalNextButton', analyticsEvent);
	};

	const onClickSkip = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, `${SCREEN_NAME}Button`, {
			buttonType: 'skip',
		});
		end('modalSkipButton', analyticsEvent);
	};

	const onClickCloseModal = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, `${SCREEN_NAME}Button`, {
			buttonType: 'cross',
		});
		end('modalCloseButton', analyticsEvent);
	};

	const modalActions: Action[] = [
		{
			onClick: onClickNext,
			text: formatMessage(messages.next),
		},
		{
			onClick: onClickSkip,
			text: formatMessage(messages.skip),
		},
	];

	const Modal = (
		<BenefitsModal
			headerContent={<Animation />}
			onClose={onClickCloseModal}
			actions={modalActions}
			heading={formatMessage(messages.modalTitle)}
			withBodyReducedTopPadding
			experimental_shouldShowPrimaryButtonOnRight
		>
			<FireScreenAnalytics />
			<Content>{formatMessage(messages.modalContentLine)}</Content>
			<ExperienceSuccess experience={yourWorkFirstStepExperience} />
		</BenefitsModal>
	);

	return (
		<ContextualAnalyticsData sourceName={SCREEN_NAME} sourceType={MODAL}>
			{fg('itsm_fsf_your_work_tour_segment') ? (
				<UFOSegment name="itsm-fsf-your-work-tour-welcome-modal">{Modal}</UFOSegment>
			) : (
				<>{Modal}</>
			)}
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled.p({
	[media.only.xxs]: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	[media.above.xs]: {
		paddingLeft: token('space.1000', '80px'),
		paddingRight: token('space.1000', '80px'),
	},
});
